import { FormattedMessage, useIntl } from 'react-intl';
import Radio from 'client/components/GDS/Radio/Radio';
import Button from 'client/components/aria/Button/Button';
import TextInput from 'client/components/GDS/TextInput/TextInput';
import { useSubmit } from 'react-router';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import store from 'client/store/store';
import { setTimedContent } from 'client/store/alertSlice/alertSlice';
import { clearCreatedEnquiry } from 'client/store/enquirySlice/enquirySlice';
import { clearCreatedWorkpack } from 'client/store/workpackSlice/workpackSlice';
import { CREATE_ENQUIRY_PAGE } from 'client/utils/routes/routeConstants';
import buildEnquiryDraftFormData from 'client/utils/helpers/Enquiry/enquiryDraftUtils';
import useAuth from 'client/utils/auth/useAuth';
import { enquiryFeatureFlag } from 'client/utils/constants/frontEndConfig';
import styles from './WorkpackTypeSelectionForm.module.scss';

const PurposeSelectionForm = ({ setPurpose, setShowSummary, enquiry, canSelectEmergency }) => {
    const intl = useIntl();
    const submit = useSubmit();
    const { user } = useAuth();

    // Initialize state from enquiry.purpose
    const getInitialPurposeState = () => {
        const purpose = enquiry?.purpose;
        if (!purpose) {
            return { selectedPurpose: null, otherDescription: '' };
        }

        // Check if it's one of the predefined purposes
        if (['prework', 'planned', 'emergency'].includes(purpose)) {
            return { selectedPurpose: purpose, otherDescription: '' };
        }

        // Otherwise it's a custom 'other' purpose
        return { selectedPurpose: 'other', otherDescription: purpose };
    };

    const initialState = getInitialPurposeState();
    const [isDisabled, setIsDisabled] = useState(true);
    const [selectedPurpose, setSelectedPurpose] = useState(initialState.selectedPurpose);
    const [otherDescription, setOtherDescription] = useState(initialState.otherDescription);

    const handleSubmitForm = () => {
        const purposeValue = selectedPurpose === 'other'
            ? otherDescription
            : selectedPurpose;
        setPurpose(purposeValue);
    };

    const handleMakeEnquiry = async () => {
        // Clear any previously created enquiry or workpack to prevent navigation issues
        store.dispatch(clearCreatedEnquiry());
        store.dispatch(clearCreatedWorkpack());

        if (!enquiry?.geometry) {
            await store.dispatch(setTimedContent(intl.formatMessage({ id: 'bookmark.i2d.drawAOI' })));
            return;
        }

        const isEmergencyPurpose = selectedPurpose === 'emergency';
        const utilityEnquiryType = isEmergencyPurpose ? 'unplanned' : 'planned';
        const startDate = isEmergencyPurpose
            ? enquiry?.startDate ?? (() => {
                const emergencyStart = new Date();
                emergencyStart.setHours(0, 0, 0, 0);
                return emergencyStart.toISOString();
            })()
            : enquiry?.startDate;

        const formData = buildEnquiryDraftFormData({
            geometry: enquiry.geometry,
            title: enquiry?.title,
            description: enquiry?.description,
            jobReference: enquiry?.jobReference,
            activityType: enquiry?.activityType,
            isEmergency: isEmergencyPurpose,
            utilityEnquiryType,
            startDate,
            endDate: isEmergencyPurpose ? null : enquiry?.endDate
        });

        submit(formData, {
            action: CREATE_ENQUIRY_PAGE,
            method: 'put'
        });
    };

    useEffect(() => {
        setShowSummary(false);
    }, [setShowSummary]);

    useEffect(() => {
        if (selectedPurpose === 'other') {
            setIsDisabled(!otherDescription || otherDescription.trim() === '');
        } else {
            setIsDisabled(selectedPurpose === null);
        }
    }, [selectedPurpose, otherDescription]);

    const handlePurposeChange = (purpose) => {
        setSelectedPurpose(purpose);
        if (purpose !== 'other') {
            setOtherDescription('');
        }
    };

    return (
        <div>
            <div className="govuk-form-group" aria-labelledby="purpose-label" data-testid="purpose-selection">
                <fieldset className="govuk-fieldset">
                    <legend className="govuk-fieldset__legend govuk-fieldset__legend--m">
                        <h2 className="govuk-fieldset__heading govuk-!-margin-top-4" id="purpose-label">
                            <FormattedMessage id='createWorkpackPage.workpack.purpose'/>
                        </h2>
                    </legend>
                    {user?.organisationName && (
                        <div className="govuk-body govuk-!-margin-bottom-6">
                            <p className="govuk-body">
                                <FormattedMessage
                                    id='createWorkpackPage.purpose.currentOrganisation'
                                    values={{ organisationName: user.organisationName }}
                                />
                            </p>
                        </div>
                    )}
                    <div className='govuk-body govuk-!-margin-top-4'>
                        <FormattedMessage id='createWorkpackPage.purpose.purpose'/>
                    </div>
                    <div id="purpose-hint" className="govuk-hint">
                        <FormattedMessage id='createWorkpackPage.purpose.hint'/>
                    </div>
                    <div className={[styles.radio, 'govuk-radios'].join(' ')} data-module="govuk-radios" role="radiogroup">
                        <Radio
                            name="purpose"
                            data-testid='radio.input-prework'
                            id={'prework-purpose'}
                            label={intl.formatMessage({ id: 'createWorkpackPage.purpose.prework' })}
                            value={'prework'}
                            role="radio"
                            tabIndex={0}
                            checked={selectedPurpose === 'prework'}
                            onChange={() => handlePurposeChange('prework')}
                        />
                        {enquiryFeatureFlag && (
                            <>
                                <Radio
                                    name="purpose"
                                    data-testid='radio.input-planned'
                                    id={'planned-purpose'}
                                    label={intl.formatMessage({ id: 'createWorkpackPage.purpose.planned' })}
                                    value={'planned'}
                                    role="radio"
                                    tabIndex={0}
                                    checked={selectedPurpose === 'planned'}
                                    onChange={() => handlePurposeChange('planned')}
                                />
                                {canSelectEmergency && (
                                    <Radio
                                        name="purpose"
                                        data-testid='radio.input-emergency'
                                        id={'emergency-purpose'}
                                        label={intl.formatMessage({ id: 'createWorkpackPage.purpose.emergency' })}
                                        value={'emergency'}
                                        role="radio"
                                        tabIndex={0}
                                        checked={selectedPurpose === 'emergency'}
                                        onChange={() => handlePurposeChange('emergency')}
                                    />
                                )}
                            </>
                        )}
                        <Radio
                            name="purpose"
                            data-testid='radio.input-other'
                            id={'other-purpose'}
                            label={intl.formatMessage({ id: 'createWorkpackPage.purpose.other' })}
                            value={'other'}
                            role="radio"
                            tabIndex={0}
                            checked={selectedPurpose === 'other'}
                            onChange={() => handlePurposeChange('other')}
                        />
                    </div>
                    {(selectedPurpose === 'planned' || selectedPurpose === 'emergency') && (
                        <div className="govuk-inset-text govuk-!-margin-top-6 govuk-grid-column-three-quarters">
                            <FormattedMessage id='createWorkpackPage.purpose.planned.notice'/>
                        </div>
                    )}
                    {selectedPurpose === 'other' && (
                        <div className="govuk-!-margin-top-8 govuk-!-margin-bottom-2">
                            <TextInput
                                id="other-description"
                                name="otherDescription"
                                label={intl.formatMessage({ id: 'createWorkpackPage.purpose.other.description.label' })}
                                description={intl.formatMessage({ id: 'createWorkpackPage.purpose.other.description.hint' })}
                                value={otherDescription}
                                onChange={(e) => setOtherDescription(e.target.value)}
                                data-testid="other-description-input"
                                width="two-thirds"
                            />
                        </div>
                    )}
                </fieldset>
            </div>
            <hr className="govuk-section-break govuk-section-break--l govuk-section-break--visible" />
            <div className={'govuk-button-group'}>
                {enquiryFeatureFlag && (selectedPurpose === 'planned' || selectedPurpose === 'emergency')
                    ? (
                        <Button
                            variant="info"
                            isDisabled={false}
                            onPress={handleMakeEnquiry}
                            data-testid='makeEnquiryButton'
                        >
                            {intl.formatMessage({ id: 'general.makeEnquiry' })}
                        </Button>
                    )
                    : (
                        <Button
                            isDisabled={isDisabled}
                            onPress={handleSubmitForm}
                            data-testid='continueButton'
                        >
                            {intl.formatMessage({ id: 'general.continue' })}
                        </Button>
                    )}
            </div>
        </div>
    );
};

PurposeSelectionForm.propTypes = {
    setPurpose: PropTypes.func.isRequired,
    setShowSummary: PropTypes.func.isRequired,
    enquiry: PropTypes.object,
    canSelectEmergency: PropTypes.bool
};

PurposeSelectionForm.defaultProps = {
    enquiry: null,
    canSelectEmergency: true
};

export default PurposeSelectionForm;
